import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import heic2any from "heic2any";
import imageCompression from 'browser-image-compression';

type FileFieldComponentProps = {
  id?: string
  className?: string
  placeholder?: string
  mb?: string
  files: Array<object>,
  setFiles: React.Dispatch<React.SetStateAction<Array<object>>>
  required?: boolean
  autoFocus?: boolean
  disabled?: boolean
}

const FileFieldStyle = styled.div<{$mb?: string, $disabled: boolean}>`
  margin-bottom: ${props => props.$mb || '0'};
  width: 100%; 
  height: 50px;
  
  button {
    background: ${props => props.$disabled ? '#696767' : '#393432'};
    border: none;
    border-radius: 25px;
    color: white;
    cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
    font-size: 12px;
    width: 100%;
    height: 100%;
    
    &:hover {
      background: #696767;
    }
  }
  
  input {
    display: none;
  }
`;

const FilesField: React.FC<FileFieldComponentProps> = ({ id, className, placeholder, mb, required, autoFocus, files, setFiles, disabled }) => {
  const [uploading, setUploading] = useState(false);
  const reader = new FileReader();
  const inputRef = useRef<HTMLInputElement>(null);
  const onClickButton = () => inputRef.current?.click();
  const compressImage = async (
    file: File,
    options = {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 1920,
    }
  ): Promise<File> => {
    try {
      return await imageCompression(file, options);
    } catch (err) {
      return Promise.reject(new Error(`compress failed: ${err}`));
    }
  };
  const onChangeInput = async () => {
    reader.addEventListener('load', () => {
      const url = reader.result.toString();
      setFiles([...files, { id: null, url: url }]);
    });

    const image = inputRef.current.files[0];
    // 画像選択をせずにキャンセルするとエラーになるので処理を中断する
    if (!image) return;
    const imageType = image.type;
    if (imageType === 'image/heic' || imageType === 'image/heif') {
      setUploading(true);
      const outputBlob = await heic2any({
        blob: image,
        toType: 'image/jpeg',
        quality: 1,
      }) as Blob;
      const jpegFile = new File([outputBlob], image.name.replace(/\.[^/.]+$/, ".jpeg"), {
        type: 'image/jpeg',
      });
      const compressedImage = await compressImage(jpegFile);
      if (!Array.isArray(compressedImage)) {
        reader.readAsDataURL(compressedImage);
        setUploading(false);
      }
    } else {
      const compressedImage = await compressImage(inputRef.current.files[0]);
      reader.readAsDataURL(compressedImage);
    }
  };

  return (
    <FileFieldStyle className={className} $mb={mb} $disabled={disabled}>
      <button type='button' onClick={onClickButton}>{ uploading ? 'アップロード中...' : placeholder }</button>
      <input type='file' id={id} ref={inputRef} required={required} autoFocus={autoFocus} onChange={onChangeInput} disabled={disabled} />
    </FileFieldStyle>
  );
};

export default FilesField;
